// ─────────────────────────────────────────────────────────────────────────────
// LEARNING AGILITY — Scoring Engine
// Input:  raw answers {questionId: 1..5}
// Output: dimension scores (0-10), overall LA score, band, profile type
// ─────────────────────────────────────────────────────────────────────────────
import { QUESTIONS } from "./questions.js";
import { DIMENSIONS, DIM_MAP, OVERALL_FORMULA, NORMS, getLABand } from "./factors.js";

export function scoreLA(answers = {}) {
  // Step 1: Collect raw scores per dimension
  const dimRaw = {};
  const dimCount = {};
  DIMENSIONS.forEach(d => { dimRaw[d.id] = 0; dimCount[d.id] = 0; });

  QUESTIONS.forEach(q => {
    const raw = parseInt(answers[q.id] || answers[String(q.id)], 10);
    if (!raw || raw < 1 || raw > 5) return;
    const score = q.reverse ? (6 - raw) : raw;
    dimRaw[q.dim] = (dimRaw[q.dim] || 0) + score;
    dimCount[q.dim] = (dimCount[q.dim] || 0) + 1;
  });

  // Step 2: Convert to 0-10 scale (max raw = 5 per item × 8 items = 40 → /40 × 10)
  const scores = {};
  DIMENSIONS.forEach(d => {
    const maxRaw = dimCount[d.id] * 5;
    scores[d.id] = maxRaw > 0
      ? +((dimRaw[d.id] / maxRaw) * 10).toFixed(1)
      : 0;
  });

  // Step 3: Overall LA score (weighted)
  const overall = +(
    Object.entries(OVERALL_FORMULA).reduce((sum, [dim, weight]) => {
      return sum + (scores[dim] || 0) * weight;
    }, 0)
  ).toFixed(1);

  // Step 4: Band
  const band = getLABand(overall);

  // Step 5: Profile type — based on pattern of high/low dimensions
  const sorted = Object.entries(scores)
    .sort((a, b) => b[1] - a[1]);
  const dominant    = sorted[0][0];
  const secondary   = sorted[1][0];
  const developing  = sorted[sorted.length - 1][0];

  // Profile label
  const profileMap = {
    "MA": "Analytical", "PA": "Relational", "CA": "Adaptive",
    "RA": "Driven",     "SA": "Reflective",
  };
  const profileType = profileMap[dominant] || "Balanced";

  // Completion
  const answered = QUESTIONS.filter(q => answers[q.id] != null || answers[String(q.id)] != null).length;
  const completion = Math.round((answered / QUESTIONS.length) * 100);

  // Norm-referenced position per dimension
  const nrp = {};
  DIMENSIONS.forEach(d => {
    const [lo, hi] = NORMS[d.id] || [5, 7];
    const s = scores[d.id];
    if (s > hi)      nrp[d.id] = "above";
    else if (s < lo) nrp[d.id] = "below";
    else             nrp[d.id] = "within";
  });

  return {
    // Core scores
    scores,         // { MA: 7.5, PA: 6.2, CA: 8.1, RA: 7.8, SA: 6.5 }
    overall,        // 7.3
    band,           // { label, color, sub }
    dominant,       // "CA"
    secondary,      // "RA"
    developing,     // "SA"
    profileType,    // "Adaptive"
    nrp,            // { MA: "above", PA: "within", ... }
    completion,     // 100

    // App.js compatibility
    scales:  scores,
    perfAvg: overall,
    potAvg:  scores.SA || 0,   // Self-Awareness as potential proxy
    perfBox: null,
    potBox:  null,
    primary: dominant,
    tertiary: sorted[2]?.[0] || null,
    least:    developing,
  };
}

export default scoreLA;
